import type { TvShow, TvShowSeason } from '@setplex/tria-api'
import type { Effect, Store } from 'effector'
import type { TvShowEpisodePayment } from '~/features/payment/interfaces'
import type { SignInToWatchItem } from '~/features/signin-to-watch'
import type { TvShowRecommendedWithPrice } from '~/shared/interfaces'
import type { ITvShowEpisodeFormat } from './index.h'

import { api } from '@setplex/tria-api'
import {
  combine,
  createEffect,
  createEvent,
  createStore,
  restore,
  sample,
} from 'effector'
import { model as i18n } from '~/core/i18n'
import { COMPONENT, register } from '~/core/repository'
import { model as routerModel } from '~/core/router'
import {
  entitiesPlayerTrailerModel,
  TrailerContentType,
} from '~/entities/player/trailer'
import { model as tvShowModel, resolveShowNewLabel } from '~/entities/shows'
import { model as tvShowSeasonsModel } from '~/entities/shows/seasons'
import { ContentWatchedEpisodeActions } from '~/features/content-watched/ui'
import {
  getEpisodePaymentExternalId,
  getSeasonPaymentExternalId,
  model as paymentModel,
} from '~/features/payment'
import { ssePurchasesModel } from '~/features/payment/sse-purchases'
import * as pinLockFeature from '~/features/pin-lock'
import { profilesFeaturesModel } from '~/features/profiles'
import { model as guestModel } from '~/features/subscriber/guest'
import {
  aye,
  exists,
  F,
  nay,
  T,
  createUnsettledDetailsLoading,
} from '~/shared/helpers'
import { extendMediaItemByLabelPrice } from '~/shared/tools'
import { model as popupModel } from '~/widgets/Popups/InfoPopup'
import { model as signInToWatchPopupModel } from '~/widgets/Popups/SignInToWatchPopup'
import {
  checkUpdatedItem,
  checkUpdatedItemBundleIds,
  formatSeasonSelectorLabel,
  formatTVShowEpisode,
  formatTVShowEpisodesLabel,
  formatTVShowSeasonEpisodesLabel,
  formatTVShowSeasonsLabel,
  isSameShowSeasonSwitch,
  isTvShowEpisodeLocked,
  seasonDetailsPath,
} from './lib'
import '~/widgets/Player/PlayerTrailer'

// *
// * EVENTS
// *

export const init = createEvent<{
  showId: string
  seasonId?: string | null
  query: URLSearchParams
}>()

const $initParams = restore(init, null)

const resetSeasonDetails = createEvent()
const resetSeasonEpisodes = createEvent()

export const left = createEvent()

export const selectSeason = createEvent<{ seasonId: number | string }>()

export const $isOnThePage = createStore(false).on(init, T).on(left, F)

export const $isDetailsPageLoading = createUnsettledDetailsLoading({
  $isOnThePage,
  $entity: tvShowModel.$currentShow,
  $pending: api.tvshows.getTvShowFx.pending,
  settled: api.tvshows.getTvShowFx.finally,
  failed: api.tvshows.getTvShowFx.fail,
  resetOn: [init, left],
})

const shouldShowAlsoMayLikeSection = (
  isKids: boolean,
  query: URLSearchParams
) =>
  query.get('fromCwActionsDetails') !== 'true' &&
  query.get('fromSearch') !== 'true' &&
  !(
    isKids &&
    !query.get('collectionId') &&
    query.get('only-favorites') !== 'true'
  )

export const $shouldShowAlsoMayLike = createStore(true).reset(left)

sample({
  clock: init,
  source: profilesFeaturesModel.$isActiveKidsProfile,
  fn: (isKids, { query }) => shouldShowAlsoMayLikeSection(isKids, query),
  target: $shouldShowAlsoMayLike,
})

const isDetailsPathname = (pathname: string | null) =>
  Boolean(pathname?.startsWith('/content/series/'))

const loadedShowSeason = {
  currentShow: api.tvshows.$currentTvShow,
  currentSeason: api.tvshows.$tvShowSeason,
}

const isLoadedShowSeasonSwitch = (
  loaded: {
    currentShow: { id?: number } | null
    currentSeason: { id?: number } | null
  },
  params: { showId: string; seasonId?: string | null }
) =>
  isSameShowSeasonSwitch({
    currentShowId: loaded.currentShow?.id,
    currentSeasonId: loaded.currentSeason?.id,
    nextShowId: params.showId,
    nextSeasonId: params.seasonId,
  })

const isExactShowPage = (
  initParams: { showId: string; seasonId?: string | null } | null,
  tvShowId: number
) => Number(initParams?.showId) === tvShowId

sample({
  clock: selectSeason,
  source: {
    show: api.tvshows.$currentTvShow,
    location: routerModel.$location,
  },
  filter: ({ show, location }, { seasonId }) =>
    show?.id != null &&
    location != null &&
    location.pathname !== seasonDetailsPath(show.id, seasonId),
  fn: ({ show, location }, { seasonId }) => ({
    pathname: seasonDetailsPath(show!.id, seasonId),
    search: location!.search,
    hash: location!.hash,
    state: { skipScrollToTop: true },
  }),
  target: routerModel.navigatePush,
})

// *
// * EFFECTS
// *

export const waitExactShowPageFx: Effect<
  {
    showId: string
    seasonId?: string
    query: URLSearchParams
  },
  TvShow[]
> = createEffect()

export const setCategoryId = createEvent<number>()
export const setShowIndex = createEvent<number>()

// *
// * STORES
// *

export const $recommended: Store<TvShowRecommendedWithPrice[]> = combine(
  api.tvshows.$recommendedTvShows,
  ssePurchasesModel.$tvShow,
  i18n.$t,
  (shows, adjust, { t }) =>
    shows.map((tvShow) => {
      const formatted = extendMediaItemByLabelPrice(
        adjust(tvShow)
      ) as TvShowRecommendedWithPrice

      return {
        ...formatted,
        newLabel: resolveShowNewLabel(formatted, t),
      }
    })
)

// current tv show season
export const $season = createStore<TvShowSeason | null>(null)
  .on(tvShowSeasonsModel.$seasonDetails, (__, payload) => payload)
  .reset(resetSeasonDetails)

// store with tv show episodes
export const $episodes: Store<ITvShowEpisodeFormat[]> = combine(
  api.tvshows.$tvShowSeasonEpisodes,
  ssePurchasesModel.$episode,
  i18n.$t,
  $season,
  formatTVShowEpisode
)

export const $isEpisodeLockedFn = combine(
  pinLockFeature.$isLockedFn,
  pinLockFeature.$unlockedId,
  tvShowModel.$currentShow,
  (isLocked, unlockedId, tvShow) => (episode: ITvShowEpisodeFormat) =>
    isTvShowEpisodeLocked({
      episode,
      isLocked,
      unlockedId,
      tvShowId: tvShow?.id,
    })
)

// TvShow seasons label
export const $seasonsLabel: Store<string> = combine(
  tvShowModel.$currentShow,
  i18n.$t,
  formatTVShowSeasonsLabel
)

// TvShow episodes label
export const $episodesLabel: Store<string> = combine(
  tvShowModel.$currentShow,
  i18n.$t,
  formatTVShowEpisodesLabel
)

export const $seasonEpisodesLabel: Store<string> = combine(
  $episodes,
  i18n.$t,
  formatTVShowSeasonEpisodesLabel
)

export const $seasonId = createStore<number | null>(null)
export const $episodeId = createStore<number | null>(null)

// *
// * CONNECTIONS
// *
const reloadPage = createEvent<void>()

// reload page if current show or season is updated
sample({
  clock: [ssePurchasesModel.$tvShowIds, ssePurchasesModel.$seasonIds],
  source: {
    currentShow: tvShowModel.$currentShow,
    season: $season,
  },
  filter: ({ currentShow, season }, ids) =>
    checkUpdatedItem({ currentShow, season, ids }),
  target: reloadPage,
})

// reload page if current show or season is updated
sample({
  clock: ssePurchasesModel.$tvShowSetIds,
  source: {
    currentShow: tvShowModel.$currentShow,
    season: $season,
  },
  filter: ({ currentShow, season }, ids) =>
    checkUpdatedItemBundleIds({ currentShow, season, ids }),
  target: reloadPage,
})

sample({
  clock: reloadPage,
  source: {
    initParams: $initParams,
    isOnThePage: $isOnThePage,
    pathname: routerModel.$pathname,
  },
  filter: ({ initParams, isOnThePage, pathname }) =>
    aye(initParams) && isOnThePage && isDetailsPathname(pathname),
  fn: ({ initParams }) => initParams!,
  target: init,
})

//
// tv show itself
//
sample({
  clock: init,
  source: loadedShowSeason,
  filter: (loaded, params) => !isLoadedShowSeasonSwitch(loaded, params),
  fn: (_, { showId }) => ({ tvShowId: Number(showId) }),
  target: [resetSeasonDetails, resetSeasonEpisodes, api.tvshows.getTvShowFx],
})

sample({
  clock: init,
  source: loadedShowSeason,
  filter: isLoadedShowSeasonSwitch,
  target: resetSeasonEpisodes,
})

//
// episodes
//

// load all episodes
sample({
  clock: api.tvshows.getTvShowFx.done,
  source: {
    initParams: $initParams,
    pathname: routerModel.$pathname,
  },
  filter: ({ initParams, pathname }, { params, result: shows }) =>
    isDetailsPathname(pathname) &&
    isExactShowPage(initParams, params.tvShowId) &&
    nay(shows?.[0]?.isWithSeason) &&
    aye(shows[0]?.id),
  fn: (_, { result: [{ id }] }) => ({ tvShowId: id, page: 0 }),
  target: api.tvshows.getTvShowEpisodesWithoutSeasonByPageFx,
})

//
// seasons
//

// load all seasons
sample({
  clock: api.tvshows.getTvShowFx.done,
  source: {
    initParams: $initParams,
    pathname: routerModel.$pathname,
  },
  filter: ({ initParams, pathname }, { params, result: shows }) =>
    isDetailsPathname(pathname) &&
    isExactShowPage(initParams, params.tvShowId) &&
    aye(shows?.[0]?.isWithSeason),
  fn: (_, { result: [{ id }] }) => ({ tvShowId: id, page: 0 }),
  target: api.tvshows.getTvShowSeasonsByPageFx,
})

// if `seasonId` parameter is specified -> load requested season
sample({
  clock: init,
  source: {
    pending: api.tvshows.getTvShowSeasonByIdFx.pending,
    pathname: routerModel.$pathname,
  },
  filter: ({ pending, pathname }, { seasonId }) =>
    nay(pending) && aye(seasonId) && isDetailsPathname(pathname),
  fn: (_, { showId, seasonId }) => ({
    tvShowId: Number(showId),
    seasonId: Number(seasonId),
    page: 0,
  }),
  target: api.tvshows.getTvShowSeasonByIdFx,
})

sample({
  clock: init,
  source: {
    pending: api.tvshows.getTvShowEpisodesBySeasonByPageFx.pending,
    pathname: routerModel.$pathname,
  },
  filter: ({ pending, pathname }, { seasonId }) =>
    nay(pending) && aye(seasonId) && isDetailsPathname(pathname),
  fn: (_, { showId, seasonId }) => ({
    tvShowId: Number(showId),
    seasonId: Number(seasonId),
    page: 0,
  }),
  target: api.tvshows.getTvShowEpisodesBySeasonByPageFx,
})

// otherwise load first season
sample({
  clock: api.tvshows.getTvShowSeasonsByPageFx.done,
  source: {
    initParams: $initParams,
    pathname: routerModel.$pathname,
  },
  filter: (
    { initParams, pathname },
    { params, result: { content: seasons } }
  ) =>
    isDetailsPathname(pathname) &&
    isExactShowPage(initParams, params.tvShowId) &&
    nay(initParams?.seasonId) &&
    seasons &&
    seasons.length > 0,
  fn: (_, { params, result: { content: seasons } }) => ({
    tvShowId: params.tvShowId,
    seasonId: seasons[0].id,
    page: 0,
  }),
  target: [
    api.tvshows.getTvShowEpisodesBySeasonByPageFx,
    api.tvshows.getTvShowSeasonByIdFx,
  ],
})

// sign in to watch logic

const selectTvShowEpisode = createEvent<
  TvShowEpisodePayment & SignInToWatchItem
>()

sample({
  clock: selectTvShowEpisode,
  source: {
    guest: guestModel.$guest,
  },
  filter: ({ guest }, { free, purchaseInfo }) =>
    aye(guest) && !free && !purchaseInfo,
  fn: (_, episode) => ({
    name: episode.name,
    redirect: episode.redirect,
  }),
  target: signInToWatchPopupModel.openSignInToWatchPopup,
})

sample({
  clock: selectTvShowEpisode,
  source: {
    guest: guestModel.$guest,
  },
  filter: ({ guest }, { free, purchaseInfo }) =>
    aye(guest) && (aye(free) || aye(purchaseInfo)),
  fn: (_, episode) => episode,
  target: paymentModel.selectTvShowEpisode,
})

sample({
  clock: selectTvShowEpisode,
  source: guestModel.$guest,
  filter: nay,
  fn: (_, episode) => episode,
  target: paymentModel.selectTvShowEpisode,
})

//
// recommendations
//

// load recommendations by `showId` and `categoryId`, if any
sample({
  clock: init,
  source: {
    pending: api.tvshows.getRecommendedTvShowsFx.pending,
    isKids: profilesFeaturesModel.$isActiveKidsProfile,
    pathname: routerModel.$pathname,
    currentShow: api.tvshows.$currentTvShow,
    currentSeason: api.tvshows.$tvShowSeason,
  },
  filter: ({ pending, isKids, pathname, currentShow, currentSeason }, params) =>
    nay(pending) &&
    shouldShowAlsoMayLikeSection(isKids, params.query) &&
    isDetailsPathname(pathname) &&
    !isLoadedShowSeasonSwitch({ currentShow, currentSeason }, params),
  fn: (_, { showId, query }) => ({
    tvShowId: Number(showId),
    categoryId: Number(query.get('categoryId') || 0),
    collectionId: query.get('collectionId') || undefined,
    onlyFavorites: query.get('only-favorites') === 'true' ? true : undefined,
    showIndex: query.has('showIndex')
      ? Number(query.get('showIndex'))
      : undefined,
  }),
  target: api.tvshows.getRecommendedTvShowsFx,
})

export const $queryShowIndex = createStore<string>('').on(
  setShowIndex,
  (_, showIndex) => (exists(showIndex) ? `&showIndex=${showIndex}` : '')
)

export const $queryCategoryId = createStore<string>('').on(
  setCategoryId,
  (_, categoryId) => (exists(categoryId) ? `&categoryId=${categoryId}` : '')
)

export const $queryCollectionId = createStore<string>('').on(
  init,
  (_, { query }) => {
    const collectionId = query.get('collectionId')
    return collectionId ? `&collectionId=${collectionId}` : ''
  }
)

export const $queryOnlyFavorites = createStore<string>('').on(
  init,
  (_, { query }) =>
    query.get('only-favorites') === 'true' ? '&only-favorites=true' : ''
)

export const $episodeQuery = api.tvshows.$tvShowSeasonEpisodes.map(
  (episodes) => {
    const id = episodes && episodes?.length > 0 ? episodes[0].id : null
    return exists(id) ? `&episodeId=${id}` : ''
  }
)

export const $seasonQuery = $season.map((season) => {
  const id = season?.id
  return exists(id) ? `&seasonId=${id}` : ''
})

const $seasonPaymentExternalId = combine(
  $season,
  tvShowModel.$currentShow,
  (season, show) => getSeasonPaymentExternalId({ season, show }) ?? null
)

const $episodePaymentExternalId = combine(
  tvShowModel.$currentShow,
  $season,
  (show, season) => (episode: ITvShowEpisodeFormat) =>
    getEpisodePaymentExternalId({ show, season, episode })
)

//
// Unlock pin locked movie
//

const unlock = createEvent()

sample({
  clock: unlock,
  source: api.tvshows.$currentTvShow,
  filter: Boolean,
  fn: ({ id, name, locked }) =>
    ({
      ...pinLockFeature.emptyPinLockState,
      id,
      name,
      locked,
    }) as pinLockFeature.IPinLockPopupProps,
  target: pinLockFeature.openPinLockPopup,
})

//
//
//

// *
// * SKIN
// *

// clear stores
api.tvshows.$currentTvShow.reset(left)
api.tvshows.$tvShowSeasonEpisodes.reset([resetSeasonEpisodes, left])
api.tvshows.$recommendedTvShows.reset(left)
sample({
  clock: init,
  target: popupModel.closeInfoPopup,
})

const formatSeasonNumber = combine(
  i18n.$t,
  ({ t }) =>
    (seasonNumber?: string) =>
      t('season_number', { number: Number(seasonNumber) })
)

const $seasonSelectorLabel = combine(i18n.$t, ({ t }) =>
  formatSeasonSelectorLabel({ t })
)

register(tvShowModel.$currentShow, '$shows/current')
register(api.tvshows.getTvShowFx.pending, '$routes/shows/details/loading')
register($seasonQuery, '$shows/showId/season')
register($episodeQuery, '$shows/showId/episode')
register($queryCategoryId, '$shows/showId/categoryId')
register($queryCollectionId, '$shows/showId/collectionId')
register($queryOnlyFavorites, '$shows/showId/onlyFavorites')
register($queryShowIndex, '$shows/showId/showIndex')
register($shouldShowAlsoMayLike, '$routes/shows/details/shouldShowAlsoMayLike')
register($recommended, '$shows/showId/recommended')
register($season, '$shows/showId/seasonId/details')
register($episodes, '$shows/showId/index/episodes')
register($isEpisodeLockedFn, '|shows/showId/isEpisodeLocked')
register(api.tvshows.$tvShowSeasons, '$shows/showId/index/seasons')
register(formatSeasonNumber, '|shows/showId/formatSeasonNumber')
register($seasonSelectorLabel, '$shows/showId/seasonSelectorLabel')
register($seasonId, '$shows/showId/seasonId')
register($episodeId, '$shows/showId/episodeId')
register($seasonsLabel, '$shows/showId/seasonsLabel')
register($episodesLabel, '$shows/showId/episodesLabel')
register($seasonEpisodesLabel, '$shows/showId/seasonEpisodesLabel')
register(selectTvShowEpisode, 'selectTvShowEpisode')
register($seasonPaymentExternalId, '$shows/showId/seasonPaymentExternalId')
register($episodePaymentExternalId, '|shows/showId/episodePaymentExternalId')
register(unlock, 'routes/shows/details/unlock')
register(selectSeason, 'routes/shows/details/selectSeason')
register(
  ContentWatchedEpisodeActions,
  'ContentWatchedEpisodeActions',
  COMPONENT
)

//
// Trailer auto-playback logic
//

// Start trailer flow when TV show data is loaded and content is not locked
sample({
  clock: api.tvshows.getTvShowFx.doneData,
  source: {
    isLockedFn: pinLockFeature.$isLockedFn,
    isFlowActive: entitiesPlayerTrailerModel.$isTrailerFlowActive,
    isOnTrailerPage: $isOnThePage,
  },
  filter: ({ isLockedFn, isFlowActive, isOnTrailerPage }, shows) => {
    if (!isOnTrailerPage) return false
    const show = shows?.[0]
    if (!show || isFlowActive) return false
    // Check if TV show has trailer and is not locked
    return Boolean(show.trailerPresent) && !isLockedFn(show)
  },
  fn: ({ isLockedFn }, shows) => ({
    contentType: TrailerContentType.TVSHOW,
    contentId: shows[0].id,
    hasTrailer: true,
    locked: isLockedFn(shows[0]),
    free: shows[0].free,
    contentName: shows[0].name,
  }),
  target: entitiesPlayerTrailerModel.initTrailerFlow,
})

// Stop trailer flow when loading new TV show
sample({
  clock: [left, api.tvshows.getTvShowFx],
  target: entitiesPlayerTrailerModel.stopTrailerFlow,
})
